import { useMemo, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { Filter, Layers, Plus, Printer } from 'lucide-react'
import { z } from 'zod'

import type { ListLotsParams } from '@/lib/api/product-lot-api'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import {
    LOT_CARD_CODE_CLASS,
    LotListCard,
} from '@/components/common/LotListCard'
import { RefreshButton } from '@/components/common/refresh-button'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { PositionPicker } from '@/components/warehouse-positions/position-picker'
import { useWarehouses } from '@/hooks/use-inventory-query'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { usePrintProductLotLabel } from '@/hooks/use-lot-label-print'
import {
    useProductLotsInfiniteQuery,
    useProductLotsQuery,
} from '@/hooks/use-product-lot-query'
import { ProductPicker } from '@/components/products/product-picker'
import { formatDateTimeVN, formatDateVN, formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

const lotsSearchSchema = z.object({
    product_id: z.string().optional(),
    warehouse_id: z.string().optional(),
    position_id: z.string().optional(),
    only_available: z.literal('1').optional(),
    page: z.coerce.number().int().min(1).optional(),
})

type SalesWarehouseLotsSearch = z.infer<typeof lotsSearchSchema>

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/sales-warehouse/lots/',
)({
    component: Page,
    validateSearch: lotsSearchSchema,
})

function Page() {
    const search = Route.useSearch()
    const navigate = useNavigate({ from: '/sales-warehouse/lots/' })
    const [filterDialogOpen, setFilterDialogOpen] = useState(false)
    const setSearch = (next: SalesWarehouseLotsSearch) => {
        void navigate({ search: () => next, replace: false })
    }
    const setPage = (p: number) =>
        setSearch({ ...search, page: p > 1 ? p : undefined })

    const params = useMemo<ListLotsParams>(
        () => ({
            page: search.page ?? 1,
            per_page: 50,
            ...(search.product_id ? { product_id: search.product_id } : {}),
            ...(search.warehouse_id
                ? { warehouse_id: search.warehouse_id }
                : {}),
            ...(search.position_id ? { position_id: search.position_id } : {}),
            ...(search.only_available
                ? { only_available: search.only_available }
                : {}),
        }),
        [search],
    )

    const isMobile = useIsMobile()

    const { data, isLoading, isFetching, refetch } = useProductLotsQuery(
        params,
        { enabled: !isMobile },
    )
    const { data: warehousesData } = useWarehouses()

    // Mobile: infinite (params bỏ page — hook tự quản)
    const infiniteParams = useMemo(
        () => ({
            per_page: 50,
            ...(search.product_id ? { product_id: search.product_id } : {}),
            ...(search.warehouse_id
                ? { warehouse_id: search.warehouse_id }
                : {}),
            ...(search.position_id ? { position_id: search.position_id } : {}),
            ...(search.only_available
                ? { only_available: search.only_available }
                : {}),
        }),
        [
            search.product_id,
            search.warehouse_id,
            search.position_id,
            search.only_available,
        ],
    )
    const infinite = useProductLotsInfiniteQuery(infiniteParams, {
        enabled: isMobile,
    })
    const mobileLots = useMemo(
        () => infinite.data?.pages.flatMap((p) => p.data ?? []) ?? [],
        [infinite.data],
    )

    const lots = data?.data ?? []
    const total = data?.meta?.total ?? 0
    const page = search.page ?? 1
    const filterActive =
        !!search.product_id ||
        !!search.warehouse_id ||
        !!search.position_id ||
        !!search.only_available ||
        page > 1
    const clearFilter = () => setSearch({})

    const warehouses = warehousesData ?? []
    const printMut = usePrintProductLotLabel()
    const renderFilters = () => (
        <>
            <div>
                <Label className="mb-1 block">
                    {m.product_lot_list_filter_product()}
                </Label>
                <ProductPicker
                    value={search.product_id ?? ''}
                    onChange={(productId) =>
                        setSearch({
                            ...search,
                            product_id: productId || undefined,
                            page: undefined,
                        })
                    }
                    placeholder={m.product_lot_list_filter_all_products()}
                    allowClear
                />
            </div>
            <div>
                <Label className="mb-1 block">
                    {m.product_lot_list_filter_warehouse()}
                </Label>
                <Select
                    value={search.warehouse_id ?? 'all'}
                    onValueChange={(v) =>
                        setSearch({
                            ...search,
                            warehouse_id: v === 'all' ? undefined : v,
                            page: undefined,
                        })
                    }
                >
                    <SelectTrigger>
                        <SelectValue
                            placeholder={m.product_lot_list_filter_all_warehouses()}
                        />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">
                            {m.product_lot_list_filter_all_warehouses()}
                        </SelectItem>
                        {warehouses.map((w) => (
                            <SelectItem key={w.id} value={w.id}>
                                {w.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div>
                <Label className="mb-1 block">
                    {m.lot_list_filter_position()}
                </Label>
                <PositionPicker
                    lotType="product"
                    value={search.position_id}
                    onChange={(positionId) =>
                        setSearch({
                            ...search,
                            position_id: positionId || undefined,
                            page: undefined,
                        })
                    }
                    placeholder={m.lot_list_filter_all_positions()}
                    allowClear
                />
            </div>
            <div className="flex items-center gap-2 md:pt-6">
                <Switch
                    checked={search.only_available === '1'}
                    onCheckedChange={(checked) =>
                        setSearch({
                            ...search,
                            only_available: checked ? '1' : undefined,
                            page: undefined,
                        })
                    }
                    id="only-available"
                />
                <Label htmlFor="only-available">
                    {m.product_lot_list_filter_only_available()}
                </Label>
            </div>
        </>
    )

    return (
        <div className="space-y-4 p-4 md:p-6">
            <ListPageHeader
                icon={<Layers className="size-5 shrink-0" />}
                title={m.product_lot_list_title()}
                utilityActions={
                    <>
                        <RefreshButton
                            onClick={() => void refetch()}
                            loading={isFetching}
                        />
                        <ClearFilterButton
                            active={filterActive}
                            onClear={clearFilter}
                        />
                        <Button
                            variant="outline"
                            size="icon"
                            className="md:hidden"
                            onClick={() => setFilterDialogOpen(true)}
                        >
                            <Filter className="size-4" />
                        </Button>
                    </>
                }
                primaryActions={
                    <Button asChild>
                        <Link
                            to="/sales-warehouse/stock-in-notes/new"
                            className="w-full"
                        >
                            <Plus className="size-4 mr-1" />
                            {m.pll_note_create_pnk()}
                        </Link>
                    </Button>
                }
            />

            <div className="hidden md:grid gap-3 md:grid-cols-4">
                {renderFilters()}
            </div>

            <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
                <DialogContent className="max-w-none w-screen h-[100svh] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden rounded-none p-4 sm:h-auto sm:w-full sm:max-w-lg sm:rounded-xl sm:p-6">
                    <DialogHeader>
                        <DialogTitle>{m.product_lot_list_title()}</DialogTitle>
                    </DialogHeader>
                    <div className="grid content-start gap-3 overflow-y-auto py-2">
                        {renderFilters()}
                    </div>
                    <DialogFooter className="flex-row gap-2 pt-2 sm:justify-end">
                        <Button
                            variant="outline"
                            className="h-11 flex-1"
                            onClick={() => {
                                clearFilter()
                                setFilterDialogOpen(false)
                            }}
                        >
                            {m.common_cancel()}
                        </Button>
                        <Button
                            className="h-11 flex-1"
                            onClick={() => setFilterDialogOpen(false)}
                        >
                            {m.common_filter_apply()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Mobile: card list + Xem thêm */}
            <div className="space-y-2 md:hidden">
                {infinite.isLoading ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.sl_lots_loading()}
                    </div>
                ) : mobileLots.length === 0 ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.product_lot_list_empty()}
                    </div>
                ) : (
                    <>
                        {mobileLots.map((lot) => {
                            const expiry = lot.expiry_date
                                ? formatDateVN(lot.expiry_date)
                                : null
                            const attributeSub = [
                                lot.warehouse_name,
                                lot.position_code,
                                expiry ? `HSD ${expiry}` : null,
                            ]
                                .filter(Boolean)
                                .join(' · ')
                            return (
                                <LotListCard
                                    key={lot.id}
                                    onClick={() =>
                                        void navigate({
                                            to: '/sales-warehouse/lots/$id',
                                            params: { id: lot.id },
                                        })
                                    }
                                    code={
                                        <span className="inline-flex items-center gap-1.5">
                                            <Link
                                                to="/sales-warehouse/lots/$id"
                                                params={{ id: lot.id }}
                                                className={LOT_CARD_CODE_CLASS}
                                            >
                                                {lot.code}
                                            </Link>
                                            {lot.discount_percent > 0 ? (
                                                <span className="inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">
                                                    CK {lot.discount_percent}%
                                                </span>
                                            ) : null}
                                        </span>
                                    }
                                    attribute={lot.product_name ?? '—'}
                                    attributeSub={
                                        attributeSub ? attributeSub : undefined
                                    }
                                    quantityAvailable={lot.quantity_available}
                                    createdAt={lot.created_at}
                                    actions={
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            aria-label={m.lot_label_print_btn()}
                                            title={m.lot_label_print_btn()}
                                            disabled={printMut.isPending}
                                            onClick={() =>
                                                printMut.mutate(lot.id)
                                            }
                                        >
                                            <Printer className="size-4" />
                                        </Button>
                                    }
                                />
                            )
                        })}
                        {infinite.hasNextPage && (
                            <Button
                                variant="outline"
                                className="w-full h-11"
                                disabled={infinite.isFetchingNextPage}
                                onClick={() => void infinite.fetchNextPage()}
                            >
                                {infinite.isFetchingNextPage
                                    ? m.list_loading_more()
                                    : m.list_load_more()}
                            </Button>
                        )}
                    </>
                )}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.sl_lots_col_code()}</TableHead>
                            <TableHead>
                                {m.product_lot_list_col_product()}
                            </TableHead>
                            <TableHead>
                                {m.product_lot_list_col_warehouse()}
                            </TableHead>
                            <TableHead>{m.lot_list_col_position()}</TableHead>
                            <TableHead className="text-right">
                                {m.sl_lots_col_available()}
                            </TableHead>
                            <TableHead>{m.pkl_lots_col_expiry()}</TableHead>
                            <TableHead>{m.sl_lots_col_created_at()}</TableHead>
                            <TableHead className="w-[80px]"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={8}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.sl_lots_loading()}
                                </TableCell>
                            </TableRow>
                        ) : lots.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={8}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.product_lot_list_empty()}
                                </TableCell>
                            </TableRow>
                        ) : (
                            lots.map((lot) => (
                                <TableRow
                                    key={lot.id}
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() =>
                                        void navigate({
                                            to: '/sales-warehouse/lots/$id',
                                            params: { id: lot.id },
                                        })
                                    }
                                >
                                    <TableCell className="font-mono text-sm">
                                        <Link
                                            to="/sales-warehouse/lots/$id"
                                            params={{ id: lot.id }}
                                            className="text-primary hover:underline"
                                        >
                                            {lot.code}
                                        </Link>
                                        {lot.discount_percent > 0 ? (
                                            <span className="ml-1.5 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">
                                                CK {lot.discount_percent}%
                                            </span>
                                        ) : null}
                                    </TableCell>
                                    <TableCell>
                                        {lot.product_name ?? '—'}
                                    </TableCell>
                                    <TableCell>
                                        {lot.warehouse_name ?? '—'}
                                    </TableCell>
                                    <TableCell className="text-sm">
                                        {lot.position_code ?? '—'}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {formatQty(lot.quantity_available)}
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                        {lot.expiry_date
                                            ? formatDateVN(lot.expiry_date)
                                            : '—'}
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                        {formatDateTimeVN(lot.created_at)}
                                    </TableCell>
                                    <TableCell
                                        className="text-right"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            title={m.lot_label_print_btn()}
                                            disabled={printMut.isPending}
                                            onClick={() =>
                                                printMut.mutate(lot.id)
                                            }
                                        >
                                            <Printer className="size-4" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination — desktop/tablet only */}
            <div className="hidden md:flex items-center justify-between">
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(Math.max(1, page - 1))}
                >
                    {m.sl_pagination_prev()}
                </Button>
                <div className="text-sm text-muted-foreground">
                    {m.sl_pagination_page({
                        page,
                        total: Math.max(1, Math.ceil(total / 50)),
                    })}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={!total || page * 50 >= total}
                    onClick={() => setPage(page + 1)}
                >
                    {m.sl_pagination_next()}
                </Button>
            </div>
        </div>
    )
}
